import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import api, { formatError } from "../lib/api";
import { useApi } from "../hooks/useApi";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";

function SweepProgress({ sweepId, onDone }) {
  const [s, setS] = useState(null);
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      const { data } = await api.get(`/admin/member-tools/invites/sweep/${sweepId}`);
      if (!alive) return;
      setS(data);
      if (data.status !== "done") setTimeout(tick, 1500); else onDone?.();
    };
    tick();
    return () => { alive = false; };
  }, [sweepId, onDone]);
  if (!s) return <Loader2 className="animate-spin" />;
  const pct = s.total ? Math.round((s.processed / s.total) * 100) : 100;
  return (
    <div className="space-y-3" data-testid="sweep-progress">
      <div className="h-2 w-full overflow-hidden rounded-full bg-gold/20"><div className="h-full bg-palm transition-[width]" style={{ width: `${pct}%` }} /></div>
      <p className="text-sm">{s.status === "done" ? "Sweep complete" : "Sending…"} — {s.processed}/{s.total}</p>
      <div className="grid grid-cols-3 gap-2 text-center text-sm">
        <div className="rounded-lg bg-palm/10 p-2"><p className="text-2xl font-bold text-palm" data-testid="sweep-sent">{s.sent}</p>emails sent</div>
        <div className="rounded-lg bg-gold/15 p-2"><p className="text-2xl font-bold text-gold" data-testid="sweep-sms">{s.sms}</p>SMS sent</div>
        <div className="rounded-lg bg-crimson/10 p-2"><p className="text-2xl font-bold text-crimson" data-testid="sweep-failed">{s.failed}</p>failed</div>
      </div>
    </div>
  );
}

export function InviteSweep({ onDone }) {
  const [days, setDays] = useState(7);
  const [never, setNever] = useState(false);
  const { data: stale, reload } = useApi(`/admin/member-tools/invites/stale?days=${days}&include_never_sent=${never}`, [days, never]);
  const [open, setOpen] = useState(false);
  const [sweepId, setSweepId] = useState(null);
  const [busy, setBusy] = useState(false);
  const count = stale?.length || 0;
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/admin/member-tools/invites/sweep?days=${days}&include_never_sent=${never}`); setSweepId(data.sweep_id); toast.success(`${data.queued} invite(s) queued`); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const close = (o) => { setOpen(o); if (!o) { setSweepId(null); reload(); } };
  return (
    <Dialog open={open} onOpenChange={close}>
      <Button size="sm" variant="outline" className="gap-1 border-crimson/40 text-crimson hover:bg-crimson/5" onClick={() => setOpen(true)} data-testid="invite-sweep-btn">
        <Send size={13} /> Resend stale invites{count ? ` (${count})` : ""}
      </Button>
      <DialogContent>
        <DialogHeader><DialogTitle>Resend unopened invites</DialogTitle></DialogHeader>
        {sweepId ? <SweepProgress sweepId={sweepId} onDone={onDone} /> : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Sends a fresh 24-hour set-password link (email + SMS where a phone is on file) to every member who has not opened their invite in the last
              <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="mx-1 rounded border border-gold/40 bg-white px-1 py-0.5 text-sm" data-testid="invite-sweep-days">
                {[3, 7, 14, 30].map((d) => <option key={d} value={d}>{d}</option>)}
              </select> days. Members whose invite was deliberately held (never sent) are skipped unless you tick below.</p>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={never} onChange={(e) => setNever(e.target.checked)} data-testid="invite-sweep-never" /> Also send to members who have <span className="font-semibold">never</span> been invited</label>
            <div className="max-h-56 overflow-y-auto rounded-xl border border-gold/20 text-sm" data-testid="invite-sweep-list">
              {(stale || []).map((m) => (
                <div key={m.id} className="flex justify-between border-b border-gold/10 px-3 py-1.5 last:border-0"><span className="truncate">{m.name} <span className="text-xs text-muted-foreground">{m.email}</span></span>
                  <span className="shrink-0 text-xs text-muted-foreground">{m.last_sent ? `sent ${new Date(m.last_sent).toLocaleDateString()}` : "never sent"}</span></div>
              ))}
              {count === 0 && <p className="p-3 text-center text-muted-foreground">No unopened invites older than {days} days.</p>}
            </div>
            <DialogFooter><Button onClick={run} disabled={busy || count === 0} className="w-full bg-crimson" data-testid="invite-sweep-run">{busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Send size={16} className="mr-2" />} Send {count} invite(s) now</Button></DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
